import { resolveDomainMappingForHost } from '@huay/shared/domainMappings'

type TenantWindow = Window & {
  __TENANT_ID__?: string
}

interface SharedDomainMapping {
  tenantId: string
  type?: 'admin' | 'user' | 'api'
  name?: string
  tier?: string
  verified?: boolean
  isPlatform?: boolean
}

type SharedDomainMappingsCacheEntry = {
  timestamp: number
  data: Record<string, SharedDomainMapping>
}

const sharedDomainMappingsCache = new Map<string, SharedDomainMappingsCacheEntry>()
const domainMappingsInFlight = new Map<string, Promise<Record<string, SharedDomainMapping>>>()
const domainMappingsRateLimitedUntil = new Map<string, number>()
const DOMAIN_MAPPINGS_RATE_LIMIT_BACKOFF_MS = 120_000

const isLocalDevelopmentHost = (hostname: string): boolean => {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('127.0.0.') ||
    hostname.includes('localhost')
  )
}

const normalizeDomainHost = (host: string): string => host.trim().toLowerCase()

const fetchSharedDomainMappings = async (
  cacheKey: string,
  urls: string[],
  ttlMs = 5 * 60 * 1000
): Promise<Record<string, SharedDomainMapping>> => {
  const cacheEntry = sharedDomainMappingsCache.get(cacheKey)
  const now = Date.now()

  if (cacheEntry && now - cacheEntry.timestamp < ttlMs) {
    return cacheEntry.data
  }

  const backoffUntil = domainMappingsRateLimitedUntil.get(cacheKey) || 0
  if (now < backoffUntil) {
    if (cacheEntry) return cacheEntry.data
    return {}
  }

  const existing = domainMappingsInFlight.get(cacheKey)
  if (existing) return existing

  const request = (async () => {
    for (const url of urls) {
      if (!url) continue

      try {
        const response = await fetch(url)
        if (response.status === 429) {
          domainMappingsRateLimitedUntil.set(cacheKey, Date.now() + DOMAIN_MAPPINGS_RATE_LIMIT_BACKOFF_MS)
          break
        }
        if (!response.ok) {
          continue
        }

        const data = await response.json()
        if (!data?.success) continue

        const rawMappings = data?.data?.mappings || {}
        const mappings: Record<string, SharedDomainMapping> = {}

        Object.entries(rawMappings).forEach(([domain, mapping]) => {
          const value = mapping as SharedDomainMapping
          mappings[normalizeDomainHost(domain)] = {
            ...value,
            verified: value.verified ?? true,
          }
        })

        sharedDomainMappingsCache.set(cacheKey, { timestamp: Date.now(), data: mappings })
        return mappings
      } catch {
        // try next url
      }
    }

    if (cacheEntry) return cacheEntry.data
    return {}
  })().finally(() => {
    domainMappingsInFlight.delete(cacheKey)
  })

  domainMappingsInFlight.set(cacheKey, request)
  return request
}

const isBrowser = typeof window !== 'undefined'

const getHostname = () => (isBrowser ? window.location.hostname : '')
const getHost = () => (isBrowser ? window.location.host : '')
const getAuthStorage = () => {
  if (!isBrowser) return null
  if (localStorage.getItem('accessToken')) return localStorage
  if (sessionStorage.getItem('accessToken')) return sessionStorage
  return localStorage
}

const getStoredUserTenantId = (): string => {
  const storage = getAuthStorage()
  if (!storage) return ''

  try {
    const rawUser = storage.getItem('user')
    const user = rawUser ? (JSON.parse(rawUser) as { tenantId?: string }) : null
    return user?.tenantId || ''
  } catch {
    return ''
  }
}

const getHostScopedTenantKey = () => {
  const host = getHost()
  return host ? `tenantId:${host}` : 'tenantId'
}

export const setRuntimeTenantId = (tenantId?: string | null) => {
  if (!isBrowser || !tenantId) return

  ;(window as TenantWindow).__TENANT_ID__ = tenantId

  try {
    localStorage.setItem(getHostScopedTenantKey(), tenantId)
    localStorage.setItem('tenantId', tenantId)
  } catch {
    // ignore storage failures
  }
}

export const clearRuntimeTenantId = () => {
  if (!isBrowser) return

  delete (window as TenantWindow).__TENANT_ID__

  try {
    localStorage.removeItem(getHostScopedTenantKey())
  } catch {
    // ignore storage failures
  }
}

export const getRuntimeTenantIdSync = (): string => {
  if (!isBrowser) return ''

  const hostname = getHostname()

  // Local dev: env / ?tenant= ชนะ cache เก่า (เช่น lenhuay ค้างใน localStorage)
  if (isLocalDevelopmentHost(hostname)) {
    const urlTenant = new URLSearchParams(window.location.search).get('tenant')?.trim()
    if (urlTenant) return urlTenant

    const envTenant =
      import.meta.env.VITE_TENANT_ID ||
      import.meta.env.VITE_FALLBACK_TENANT_ID ||
      (import.meta.env.PROD ? '' : 'test-demo-01')
    if (envTenant) return envTenant
  }

  const runtimeTenantId = (window as TenantWindow).__TENANT_ID__
  if (runtimeTenantId) return runtimeTenantId

  try {
    const hostScopedTenantId = localStorage.getItem(getHostScopedTenantKey())
    if (hostScopedTenantId) return hostScopedTenantId
  } catch {
    // ignore storage failures
  }

  if (isLocalDevelopmentHost(hostname)) {
    const authTenantId = getStoredUserTenantId()
    if (authTenantId) return authTenantId
  }

  return ''
}

export const resolveRuntimeTenantId = async (): Promise<string> => {
  const syncTenantId = getRuntimeTenantIdSync()
  if (syncTenantId) return syncTenantId

  if (!isBrowser) return ''

  const hostname = getHostname()
  if (isLocalDevelopmentHost(hostname)) {
    return getRuntimeTenantIdSync()
  }

  const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://gw.api-luck66.com'
  const mappings = await fetchSharedDomainMappings(
    'user-modern-public',
    [`${apiBase}/api/v1/tenant-management/domain-mappings`]
  )
  const mappedTenantId = resolveDomainMappingForHost(mappings, hostname)?.tenantId || ''
  if (mappedTenantId) {
    setRuntimeTenantId(mappedTenantId)
  }
  return mappedTenantId
}

/** Tenant for user chat API/WebSocket — prefer domain-resolved runtime over JWT. */
export function resolveUserChatTenantId(userTenantId?: string | null): string {
  return getRuntimeTenantIdSync() || userTenantId || getStoredUserTenantId() || ''
}
